export class AgentWidgetAPI {
  private baseUrl: string
  private apiKey: string

  constructor(baseUrl: string, apiKey: string) {
    this.baseUrl = baseUrl.replace(/\/$/, "")
    this.apiKey = apiKey
  }

  async sendMessage(agentId: string, message: string, conversationId?: string, user?: { id?: string; name?: string,email:string }) {
    const response = await fetch(`${this.baseUrl}/api/chat/agents/${agentId}/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": this.apiKey,
      },
      body: JSON.stringify({
        message,
        conversationId,
        user,
      }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: "Network error" }))
      throw new Error(error.error || "Failed to send message")
    }

    return response.json()
  }

  createEventSource(agentId: string, conversationId: string) {
    const url = new URL(`${this.baseUrl}/api/chat/agents/${agentId}/stream`)
    url.searchParams.set("conversationId", conversationId)
    url.searchParams.set("apiKey", this.apiKey)

    return new EventSource(url.toString(), {
      withCredentials: false,
    })
  }

  async getProducts(agentId: string) {
    const response = await fetch(`${this.baseUrl}/api/chat/agents/${agentId}/products?apiKey=${this.apiKey}`)
    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.statusText}`)
    }
    return response.json()
  }

  async createOrder(
    agentId: string,
    productId: string,
    conversationId: string,
    customerName: string,
    customerEmail: string,
    deliveryAddress?: any
  ) {
    const url = `${this.baseUrl}/api/chat/agents/${agentId}/orders?apiKey=${this.apiKey}`
    
    const body = {
      productId,
      conversationId,
      customerName,
      customerEmail,
      deliveryAddress,
    }

    console.log('Creating order with URL:', url)
    console.log('Order data:', body)

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    })

    console.log('Order response status:', response.status)

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Order creation failed:', response.status, errorText)
      throw new Error(`Failed to create order: ${response.status} ${errorText}`)
    }

    const result = await response.json()
    console.log('Order created successfully:', result)
    return result
  }

  async createCartOrder(
    agentId: string,
    conversationId: string,
    customerName: string,
    customerEmail: string,
    amount: number,
    currency: string,
    itemName: string,
    itemType: string,
    cartItems: any[]
  ) {
    const url = `${this.baseUrl}/api/chat/agents/${agentId}/orders?apiKey=${this.apiKey}`
    
    const body = {
      conversationId,
      customerName,
      customerEmail,
      amount,
      currency,
      itemName,
      itemType
    }

    console.log('Creating cart order with URL:', url)
    console.log('Cart order data:', body)

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    })

    console.log('Cart order response status:', response.status)

    if (!response.ok) {
      const errorText = await response.text()
      console.error('Cart order creation failed:', response.status, errorText)
      throw new Error(`Failed to create cart order: ${response.status} ${errorText}`)
    }

    const result = await response.json()
    console.log('Cart order created successfully:', result)
    return result
  }

  async completePaymentOrder(agentId: string, orderId: string, paymentReference: string) {
    const response = await fetch(`${this.baseUrl}/api/chat/agents/${agentId}/orders/${orderId}/complete?apiKey=${this.apiKey}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        paymentReference,
      }),
    })
    if (!response.ok) {
      throw new Error(`Failed to complete payment: ${response.statusText}`)
    }
    return response.json()
  }
  async completePaymentBooking(agentId: string, bookingId: string, paymentReference: string) {
    const response = await fetch(`${this.baseUrl}/api/chat/agents/${agentId}/bookings/${bookingId}/complete?apiKey=${this.apiKey}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        paymentReference,
      }),
    })
    if (!response.ok) {
      throw new Error(`Failed to complete payment: ${response.statusText}`)
    }
    return response.json()
  }

  async getOrders(agentId: string, customerEmail: string) {
    const response = await fetch(`${this.baseUrl}/api/chat/agents/${agentId}/orders?apiKey=${this.apiKey}&customerEmail=${encodeURIComponent(customerEmail)}`)
    if (!response.ok) {
      throw new Error(`Failed to fetch orders: ${response.statusText}`)
    }
    return response.json()
  }

  async getServices(agentId: string) {
    const response = await fetch(`${this.baseUrl}/api/chat/agents/${agentId}/services?apiKey=${this.apiKey}`)
    if (!response.ok) {
      throw new Error(`Failed to fetch services: ${response.statusText}`)
    }
    return response.json()
  }

  async createBooking(agentId: string, bookingData: any) {
    const response = await fetch(`${this.baseUrl}/api/chat/agents/${agentId}/bookings?apiKey=${this.apiKey}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...bookingData, agentId }),
    })
    if (!response.ok) {
      throw new Error(`Failed to create booking: ${response.statusText}`)
    }
    return response.json()
  }

  async getBookings(agentId: string, customerEmail: string) {
    const response = await fetch(`${this.baseUrl}/api/chat/agents/${agentId}/bookings?apiKey=${this.apiKey}&customerEmail=${encodeURIComponent(customerEmail)}`)
    if (!response.ok) {
      throw new Error(`Failed to fetch bookings: ${response.statusText}`)
    }
    return response.json()
  }

  async setCustomerInfo(agentId: string, conversationId: string, userInfo: { id?: string; name?: string; email?: string }) {
    const response = await fetch(`${this.baseUrl}/api/chat/agents/${agentId}/customer`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": this.apiKey,
      },
      body: JSON.stringify({
        conversationId,
        customerName: userInfo.name,
        customerEmail: userInfo.email,
        customerId: userInfo.id,
      }),
    })

    if (!response.ok) {
      throw new Error(`Failed to set customer info: ${response.statusText}`)
    }

    return response.json()
  }

  async endConversation(agentId: string, conversationId: string) {
    const response = await fetch(`${this.baseUrl}/api/chat/agents/${agentId}/conversations/${conversationId}/end`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": this.apiKey,  // make sure this matches middleware
      },
      body: JSON.stringify({}) // some middleware requires body, even if empty
    })

    if (!response.ok) {
      throw new Error(`Failed to end conversation: ${response.statusText}`)
    }

    return response.json()
  }
}
